import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { civilLawContracts, type CivilLawContract } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATE_LABEL: Record<string, string> = {
  DRAFT: 'Чернетка', POSTED: '✓ Проведено', CANCELLED: '✗ Скасовано',
};

type ActiveFilter = 'all' | 'active' | 'inactive';

export default function CivilLawContractsPage() {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState('');
  const [q, setQ] = useState('');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const list = useQuery({
    queryKey: ['civil-law-contracts'],
    queryFn: () => civilLawContracts.list({ limit: 500 }),
  });

  // Клієнтська фільтрація завантаженого списку (орг / ПІБ / чинність).
  // Чинний = проведений (POSTED). Не чинний = чернетка/скасований.
  const rows = useMemo(() => {
    const all = list.data?.items ?? [];
    const ql = q.trim().toLowerCase();
    return all.filter((r) => {
      if (orgId && r.organization_id !== orgId) return false;
      if (ql && !(r.employee_name ?? '').toLowerCase().includes(ql)) return false;
      if (activeFilter === 'active' && r.state !== 'POSTED') return false;
      if (activeFilter === 'inactive' && r.state === 'POSTED') return false;
      return true;
    });
  }, [list.data, orgId, q, activeFilter]);

  const cols = useMemo<ColumnDef<CivilLawContract>[]>(() => [
    { key: 'document_number', header: '№', width: 130, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    { key: 'document_date', header: 'Дата', width: 150, sortValue: (r) => r.document_date ?? '', cell: (r) => r.document_date ? new Date(r.document_date).toLocaleDateString('uk-UA') : '' },
    { key: 'employee_name', header: 'Працівник', sortValue: (r) => r.employee_name ?? '', cell: (r) => r.employee_name },
    { key: 'date_from', header: 'З', width: 120, sortValue: (r) => r.date_from ?? '', cell: (r) => r.date_from ? new Date(r.date_from).toLocaleDateString('uk-UA') : '' },
    { key: 'date_to', header: 'По', width: 120, sortValue: (r) => r.date_to ?? '', cell: (r) => r.date_to ? new Date(r.date_to).toLocaleDateString('uk-UA') : '' },
    { key: 'total_amount', header: 'Сума', width: 130, align: 'right', sortValue: (r) => Number(r.total_amount), cell: (r) => fmt2(r.total_amount) },
    { key: 'state', header: 'Стан', width: 130, sortValue: (r) => r.state ?? '', cell: (r) => STATE_LABEL[r.state] ?? r.state },
  ], []);

  const selStyle = { width: 140, flex: '0 0 auto' } as const;

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Договори ЦПХ</h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={selStyle}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
          ))}
        </select>
        <select value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value as ActiveFilter)}
                style={selStyle}>
          <option value="all">Усі</option>
          <option value="active">Лише чинні</option>
          <option value="inactive">Лише не чинні</option>
        </select>
        <input placeholder="ПІБ працівника…" value={q}
               onChange={(e) => setQ(e.target.value)}
               style={{ width: 160, flex: '0 0 auto' }} />
        <button className="primary" style={{ marginLeft: 'auto' }}
                onClick={() => navigate('/employees/new-unified')}>
          + Новий
        </button>
      </div>
      <DataTable<CivilLawContract>
        rows={rows}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/civil-law-contracts/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </div>
  );
}
